import { Component, OnInit } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { Device, DeviceContract, DeviceStatus, DeviceStatusExt, DeviceVehicleHistory } from '@shared/models/device.model'
import { DevicesService } from '@shared/modules/devices/devices.service'
import { DestroyService } from '@core/services/destroy.service'

@Component({
  selector: 'lin-device-replace-dialog',
  templateUrl: './device-replace-dialog.component.html',
  styleUrls: ['./device-replace-dialog.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class DeviceReplaceDialogComponent implements OnInit {
  form: FormGroup
  isSubmitting = false
  oldDevice: Device
  newDevice: Device
  oldDeviceLastVehicle: DeviceVehicleHistory

  private oldImei$ = new BehaviorSubject<string>(null)
  private newImei$ = new BehaviorSubject<string>(null)

  get bothDevicesFilled(): boolean {
    return !!this.oldDevice && !!this.newDevice
  }

  get oldDeviceIsOffline(): boolean {
    return this.oldDevice.statusExt === DeviceStatusExt.Offline
  }

  get oldDeviceInStock(): boolean {
    return this.oldDevice.status === DeviceStatus.InStock
  }

  get newDeviceInStock(): boolean {
    return [DeviceStatus.InStockAdmin, DeviceStatus.InStockReseller].includes(this.newDevice.status)
  }

  get newDeviceIsInstalledInOldDeviceVehicle(): boolean {
    return this.newDevice.deviceInstallation && this.newDevice.deviceInstallation.vehicleId === this.oldDeviceLastVehicle?.vehicleId
  }

  get devicesAreValid(): boolean {
    return (
      this.bothDevicesFilled &&
      this.oldDeviceIsOffline &&
      ((!this.oldDevice.deviceInstallation && this.oldDeviceInStock && this.newDeviceIsInstalledInOldDeviceVehicle) ||
        (this.oldDevice.deviceInstallation && this.newDeviceInStock))
    )
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly devicesService: DevicesService,
    private readonly destroyService: DestroyService,
    public dialogRef: MatDialogRef<DeviceReplaceDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.createForm()
    this.subscribeToImeis()
  }

  createForm(): void {
    this.form = this.fb.group({
      imeiOld: ['', [Validators.required]],
      imeiNew: ['', [Validators.required]],
      reason: ['', [Validators.required]],
    })

    this.form
      .get('imeiOld')
      .valueChanges.pipe(takeUntil(this.destroyService))
      .subscribe((imei: string) => this.handleValueChanges(imei, 'old'))

    this.form
      .get('imeiNew')
      .valueChanges.pipe(takeUntil(this.destroyService))
      .subscribe((imei: string) => this.handleValueChanges(imei, 'new'))
  }

  handleValueChanges(imei: string, type: 'old' | 'new'): void {
    if (!imei) {
      this[`${type}Device`] = null
      type === 'old' && (this.oldDeviceLastVehicle = null)
      return
    }

    if (this[`${type}Device`] && imei === this[`${type}Device`].imei) return

    this[`${type}Imei$`].next(imei)
  }

  subscribeToImeis(): void {
    this.oldImei$
      .pipe(
        filter(Boolean),
        tap(() => this.oldDevice && (this.oldDevice = null)),
        tap(() => this.oldDeviceLastVehicle && (this.oldDeviceLastVehicle = null)),
        switchMap((imei: string) => this.fetchOldDevices(imei)),
        filter((device) => !device.deviceInstallation),
        switchMap((device) => this.devicesService.getDeviceContractHistoryList(device.id, DeviceContract.InstallationHistory, 1, 1)),
        tap((history) => (this.oldDeviceLastVehicle = <DeviceVehicleHistory>(<unknown>history[0]))),
        takeUntil(this.destroyService),
      )
      .subscribe()

    this.newImei$
      .pipe(
        filter(Boolean),
        tap(() => this.newDevice && (this.newDevice = null)),
        switchMap((imei: string) => this.fetchNewDevices(imei)),
        takeUntil(this.destroyService),
      )
      .subscribe()
  }

  fetchOldDevices(imei: string): Observable<Device> {
    return this.fetchDevices(imei).pipe(tap((device) => (this.oldDevice = device)))
  }

  fetchNewDevices(imei: string): Observable<Device> {
    return this.fetchDevices(imei).pipe(tap((device) => (this.newDevice = device)))
  }

  fetchDevices(imei: string): Observable<Device> {
    return this.devicesService
      .getDevices(0, 100, null, { imei }, ['id', 'imei', 'contractFinishAt', 'deviceInstallation', 'status', 'statusExt'])
      .pipe(
        filter((devices) => devices && devices.data.length === 1),
        map((devices) => devices.data[0]),
      )
  }

  replaceDevice(): void {
    this.isSubmitting = true
    const reason = <string>this.form.get('reason').value

    this.devicesService
      .replaceDevice(this.oldDevice.id, this.newDevice.id, reason)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe((data) => this.dialogRef.close(data))
  }
}
